import type { OCGTerritory } from '@src/store/analysis/data-typing';
import type {
  FlowAnalysisState,
} from '@src/store/calculators/FlowCalculatorStore';
import type { FunctionAnalysisState } from '@src/store/calculators/FunctionCalculatorStore';

import type {
  DerivedAction,
  FieldState,
  FieldValue,
  Relationship,
  ReportTemplate,
  ValuesMap,
} from './report-typing';

import { useAnalysisStore } from '@src/store/analysis';
import {
  useFlowCalculatorStore,
} from '@src/store/calculators/FlowCalculatorStore';
import {
  useFunctionCalculatorStore,
} from '@src/store/calculators/FunctionCalculatorStore';
import { ContourLabel } from '@src/store/contours/contour-definitions';
import { useCurrentStudyStore } from '@src/store/dicom-web/current-study-store';

import { collectFields, getRelationshipParentKeys, getValue } from './report-typing';

// ---------- Resolution context ----------
// `store` is the same shape as your Pinia/Vue ref — Record<string, FieldState>.
// Because FieldState already merges the field's structure (key, relationship,
// type...) with its live value, the store alone is enough: no separate
// Field[] list and ValuesMap to keep in sync. Resolvers read AND write into
// it directly; if `store` came from a Vue ref, pass `values.value` and
// mutations are picked up by reactivity automatically.

export interface DicomClient {
  read: (sourceKey: string, scopeValue?: FieldValue) => Promise<FieldValue>;
}

export interface AnalysisClient {
  read: (
    pipelineId: string,
    sourceKey: string,
    scopeValue?: FieldValue,
  ) => Promise<FieldValue>;
}

export interface ResolutionContext {
  store: Record<string, FieldState>;
  currentUser?: { id: string; name: string };
  dicom?: DicomClient;
  analysis?: AnalysisClient;
}

// ---------- Derived ----------

type NumericDerivedFn = (nums: number[]) => number | null;
type AgeDerivedFn = (dates: FieldValue[]) => number | null;

type DeriveRegistry = {
  [K in Exclude<DerivedAction, 'age'>]: NumericDerivedFn;
} & {
  age: AgeDerivedFn;
};

const DERIVE: DeriveRegistry = {
  sum: nums => (nums.length === 0 ? null : nums.reduce((a, b) => a + b, 0)),
  mean: nums => (nums.length === 0 ? null : nums.reduce((a, b) => a + b, 0) / nums.length),
  difference: nums => (nums.length < 2 ? null : (nums[0] ?? 0) - (nums[1] ?? 0)),
  min: nums => (nums.length === 0 ? null : Math.min(...nums)),
  max: nums => (nums.length === 0 ? null : Math.max(...nums)),
  age: (dates) => {
    const dobVal = dates[0];
    const scanDateVal = dates[1];
    if (typeof dobVal !== 'string' || !dobVal) return null;

    const parseDate = (dStr: string) => {
      if (!dStr) return new Date();
      const match = dStr.match(/^(\d{4})[-/]?(\d{2})[-/]?(\d{2})/);
      if (match) {
        return new Date(
          Number(match[1]),
          Number(match[2]) - 1,
          Number(match[3]),
        );
      }
      return new Date(dStr);
    };

    const birth = parseDate(dobVal);
    if (Number.isNaN(birth.getTime())) return null;

    const scanStr = typeof scanDateVal === 'string' ? scanDateVal : '';
    let targetDate = parseDate(scanStr);
    if (Number.isNaN(targetDate.getTime())) {
      targetDate = new Date();
    }

    let age = targetDate.getFullYear() - birth.getFullYear();
    const m = targetDate.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && targetDate.getDate() < birth.getDate())) {
      age--;
    }
    return age >= 0 ? age : 0;
  },
};

function asNumber(v: FieldValue): number | null {
  if (typeof v === 'number') return v;
  if (v === null || v === undefined || v === '' || typeof v === 'object' || typeof v === 'boolean') return null;
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
}

async function resolveDerived(
  relationship: Extract<Relationship, { kind: 'derived' }>,
  ctx: ResolutionContext,
): Promise<FieldValue> {
  if (relationship.action === 'age') {
    const args = relationship.parentKeys.map(
      key => ctx.store[key]?.value ?? null,
    );
    if (args.some(v => v === null || v === undefined || v === ''))
      return null;
    return DERIVE.age(args);
  }

  const nums = relationship.parentKeys
    .map(key => asNumber(ctx.store[key]?.value ?? null))
    .filter((n): n is number => n !== null);

  // Not enough data yet (e.g. parents not filled in) — leave it unresolved
  // rather than guessing.
  if (nums.length < relationship.parentKeys.length) return null;

  return DERIVE[relationship.action](nums);
}

function formatDicomDateToISO(dicomDate: string | undefined | null): string {
  if (!dicomDate || typeof dicomDate !== 'string') {
    return '';
  }
  const clean = dicomDate.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(clean)) {
    return clean;
  }
  if (/^\d{8}$/.test(clean)) {
    return `${clean.slice(0, 4)}-${clean.slice(4, 6)}-${clean.slice(6, 8)}`;
  }
  const d = new Date(clean);
  if (!Number.isNaN(d.getTime())) {
    return d.toISOString().slice(0, 10);
  }
  return '';
}

// ---------- Sourced ----------

async function resolveSourced(
  relationship: Extract<Relationship, { kind: 'sourced' }>,
  ctx: ResolutionContext,
): Promise<FieldValue> {
  const { source } = relationship;

  switch (source.kind) {
    case 'system': {
      const now = new Date();
      switch (source.field) {
        case 'date':
          return now.toISOString().slice(0, 10);
        case 'time':
          return now.toISOString().slice(11, 19);
        case 'user': {
          if (ctx.currentUser) {
            return ctx.currentUser.name;
          }
          const { useAppState } = await import('@src/store/ocg-app-state');
          const appState = useAppState();
          return appState.currentUserName ?? null;
        }
        default:
          return null;
      }
    }

    case 'dicom': {
      if (ctx.dicom) {
        const scopeValue = source.parentKey
          ? ctx.store[source.parentKey]?.value
          : undefined;
        return ctx.dicom.read(source.sourceKey, scopeValue);
      }

      const currentStudyStore = useCurrentStudyStore();
      const studyInfo = currentStudyStore.getStudyInfo;
      if (!studyInfo) return null;

      switch (source.sourceKey) {
        case 'PatientName':
          return studyInfo.PatientName || studyInfo.PatientID || 'Client Name';
        case 'PatientID':
          return studyInfo.PatientID || 'Client ID';
        case 'PatientSex':
          if (studyInfo.PatientSex) {
            const sex = studyInfo.PatientSex.toUpperCase();

            return sex === 'M' ? 'male' : sex === 'F' ? 'female' : 'other';
          }
          return 'other';
        case 'Manufacturer':
          return studyInfo.Manufacturer || 'Philips Ingenia';
        case 'StudyDate':
          return formatDicomDateToISO(studyInfo.StudyDate);
        case 'PatientBirthDate':
          return formatDicomDateToISO(studyInfo.PatientBirthDate);
        case 'MagneticFieldStrength':
          if (studyInfo.MagneticFieldStrength) {
            const strength = String(studyInfo.MagneticFieldStrength).trim();
            if (strength) {
              return strength.endsWith('T') || strength.endsWith('t')
                ? strength
                : `${strength}T`;
            }
          }
          return '';
        default: {
          const val = (studyInfo as Record<string, unknown>)[source.sourceKey];
          return (typeof val === 'string' || typeof val === 'number') ? val : null;
        }
      }
    }

    case 'analysis': {
      const { pipelineId, sourceKey } = source;
      const analysisStore = useAnalysisStore();
      const biomarkerContainer = analysisStore.getBiomarkerContainer();
      if (!biomarkerContainer) return null;

      if (sourceKey.toLowerCase() === 'normal') {
        const range = biomarkerContainer.getBiomarkerNormalRangeLabel(
          pipelineId,
        );
        if (!range) return '-';
        return range;
      }

      let territory = sourceKey;
      if (territory.toUpperCase() === 'LCX') {
        territory = 'LCx';
      }

      const validTerritories: OCGTerritory[] = ['Global', 'LAD', 'RCA', 'LCx'];
      const matchedTerritory = validTerritories.find(
        t => t.toLowerCase() === territory.toLowerCase(),
      );

      if (!matchedTerritory) {
        return null;
      }

      const value = biomarkerContainer.getAHATerritoryMeanOfSlices(
        pipelineId,
        matchedTerritory,
      );

      if (value === null || !Number.isFinite(value)) {
        return null;
      }
      return Number(value.toFixed(2));
    }

    case 'function-table': {
      const { axis, sourceKey } = source;
      const functionStore = useFunctionCalculatorStore();
      const functionAnalysisState = functionStore.functionAnalysisStates[axis];
      if (!functionAnalysisState || !(sourceKey in functionAnalysisState)) return null;

      const val = functionAnalysisState[sourceKey as keyof FunctionAnalysisState];
      if (
        typeof val !== 'number'
        || !Number.isFinite(val)
      ) {
        return null;
      }
      return Number(val.toFixed(2));
    }

    case 'flow-table': {
      const { roi, sourceKey } = source;
      const flowStore = useFlowCalculatorStore();
      if (
        sourceKey === 'appliedCorrections'
        || sourceKey === 'AppliedCorrections'
      ) {
        return flowStore.appliedCorrections;
      }
      const flowAnalysisState = flowStore.flowAnalysisStates[roi as ContourLabel];
      if (!flowAnalysisState || !(sourceKey in flowAnalysisState)) return null;

      const val = flowAnalysisState[sourceKey as keyof FlowAnalysisState];
      if (typeof val === 'string') return val;
      if (
        typeof val !== 'number'
        || !Number.isFinite(val)
      ) {
        return null;
      }
      return Number(val.toFixed(2));
    }

    default:
      return null;
  }
}
// ---------- Single entry point ----------

/**
 * Resolves any field's relationship into a concrete value.
 * Returns null if it can't be resolved yet (missing parents, no client
 * wired up, etc.) — callers should treat null as "leave the existing
 * value alone", not as "clear the field".
 */
export async function resolveRelationship(
  relationship: Relationship,
  ctx: ResolutionContext,
): Promise<FieldValue> {
  return relationship.kind === 'derived'
    ? resolveDerived(relationship, ctx)
    : resolveSourced(relationship, ctx);
}

// ---------- Wiring it into the store ----------
// Three attachment points: seed the store on load, recompute dependents
// on edit, and resolve on-demand for manually-triggered fields.

/** Flattens a store back down to a plain key->value snapshot for saving. */
export function toValuesMap(store: Record<string, FieldState>): ValuesMap {
  const values: ValuesMap = {};
  for (const [key, field] of Object.entries(store)) {
    values[key] = field.value;
  }
  return values;
}

/**
 * Builds the initial store from a template, applying any saved values
 * (or each field's defaultValue) — this is the one place ValuesMap is
 * still useful, as the plain key->value shape you'd persist/load, as
 * opposed to the live FieldState shape you compute against.
 */
export function createInitialStore(
  template: ReportTemplate,
  savedValues: ValuesMap = {},
): Record<string, FieldState> {
  const store: Record<string, FieldState> = {};
  for (const field of collectFields(template)) {
    store[field.key] = { ...field, value: getValue(savedValues, field) };
  }
  return store;
}

/**
 * Reverse index: parent field key -> fields whose relationship depends on
 * it. Build this once per store (it only changes if the report's
 * structure changes, not when values change) and reuse it on every edit.
 */
export function buildDependencyMap(
  store: Record<string, FieldState>,
): Map<string, FieldState[]> {
  const map = new Map<string, FieldState[]>();
  for (const field of Object.values(store)) {
    if (!field.relationship) continue;
    for (const parentKey of getRelationshipParentKeys(field.relationship)) {
      map.set(parentKey, [...(map.get(parentKey) ?? []), field]);
    }
  }
  return map;
}

/**
 * Resolves every automatic relationship in the store and writes results
 * straight into it. Call this once right after createInitialStore.
 */
export async function resolveAutomaticFields(
  ctx: ResolutionContext,
): Promise<void> {
  // Pass 1: Resolve all sourced fields first
  for (const field of Object.values(ctx.store)) {
    if (!field.relationship || field.relationship.isAutomatic === false)
      continue;
    if (field.relationship.kind === 'sourced') {
      const resolved = await resolveRelationship(field.relationship, ctx);
      if (resolved !== null) {
        field.value = resolved;
        if (
          field.options
          && !field.options.some(opt => opt.label === resolved)
        ) {
          field.options.push({ id: String(resolved), label: String(resolved) });
        }
      }
    }
  }

  // Pass 2: Iteratively resolve derived fields until fixed point
  const derivedFields = Object.values(ctx.store).filter(
    f =>
      f.relationship
      && f.relationship.isAutomatic !== false
      && f.relationship.kind === 'derived',
  );

  let changed = true;
  let iterations = 0;
  const maxIterations = 10;

  while (changed && iterations < maxIterations) {
    changed = false;
    iterations++;

    for (const field of derivedFields) {
      const resolved = await resolveRelationship(field.relationship!, ctx);
      if (resolved !== null && resolved !== field.value) {
        field.value = resolved;
        changed = true;
        if (
          field.options
          && !field.options.some(opt => opt.label === resolved)
        ) {
          field.options.push({ id: String(resolved), label: String(resolved) });
        }
      }
    }
  }
}

/**
 * Call this from your "user edited a field" action/mutation. Sets `key`'s
 * value and recomputes anything that depends on it, writing straight into
 * the store — no reassignment needed, Vue's reactivity picks up the
 * in-place mutation on its own.
 *
 * Only walks one level of dependents. If you have chained derivations
 * (A feeds B feeds C), call this again with each newly-resolved key, or
 * extend it to recurse.
 */
export async function applyFieldChange(
  key: string,
  newValue: FieldValue,
  deps: Map<string, FieldState[]>,
  ctx: ResolutionContext,
): Promise<void> {
  ctx.store[key].value = newValue;

  for (const field of deps.get(key) ?? []) {
    if (!field.relationship || field.relationship.isAutomatic === false)
      continue;

    const resolved = await resolveRelationship(field.relationship, ctx);
    if (resolved !== null) field.value = resolved;
  }
}

/**
 * Call this from an explicit trigger — e.g. the "Auto-fill" action on a
 * RichTextSection — for a field whose relationship has `isAutomatic: false`.
 */
export async function resolveFieldNow(
  key: string,
  ctx: ResolutionContext,
): Promise<void> {
  const field = ctx.store[key];
  if (!field?.relationship) return;
  const resolved = await resolveRelationship(field.relationship, ctx);
  if (resolved !== null) field.value = resolved;
}
